import React, { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import locationContext from "../../../app/providers/location/locationContext";
import { Autocomplete as MUIAutocomplete, TextField, InputAdornment, IconButton, Box, Typography, ListItem} from "@mui/material";
import PlaceIcon from "@mui/icons-material/Place";
import LocationSearchingRoundedIcon from "@mui/icons-material/LocationSearchingRounded";
import ClearIcon from "@mui/icons-material/Clear";
import { useJsApiLoader } from "@react-google-maps/api";
import { googleMapsLibraries, googleMapsApiKey} from "../../../shared/configs/googleMaps";
import { useLazyFetchAddressQuery } from "../../../app/store/api/googleMapsApi";
import CircularProgress from "@mui/material/CircularProgress";

const DEBOUNCE_DELAY = 500;
const MIN_SEARCH_LENGTH = 3;

export default function UserLocationV2({ brandsLoading }) {
  const { t } = useTranslation();
  const [address, setAddress] = useState("");
  const [isFetchingSuggestions, setIsFetchingSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const sessionTokenRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const requestIdRef = useRef(0);
  const [isGettingCurrentLocation, setIsGettingCurrentLocation] = useState(false);

  const { state: locationState, setLocation, emptyLocation, setLocationAddress, setLocationFromGeolocation } = useContext(locationContext);

  const [fetchAddress] = useLazyFetchAddressQuery();

  const { isLoaded } = useJsApiLoader({ googleMapsApiKey, libraries: googleMapsLibraries });

  useEffect(() => {
    if (locationState.locationAddress) {
      const formattedAddress = locationState.locationAddress?.formatted_address || locationState.locationAddress;

      setAddress(formattedAddress);
      setInputValue(formattedAddress);
    }
  }, [locationState.locationAddress]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const getSessionToken = async () => {
    if (!window.google?.maps?.places) {
      return null;
    }

    const { AutocompleteSessionToken } = await window.google.maps.importLibrary("places");

    if (!sessionTokenRef.current) {
      sessionTokenRef.current = new AutocompleteSessionToken();
    }

    return sessionTokenRef.current;
  };

  const fetchSuggestions = async (value) => {
    const trimmedValue = value?.trim();

    if (!trimmedValue || trimmedValue.length < MIN_SEARCH_LENGTH) {
      setSuggestions([]);
      setIsFetchingSuggestions(false);
      return;
    }

    const currentRequestId = ++requestIdRef.current;

    setIsFetchingSuggestions(true);

    try {
      const { AutocompleteSuggestion } = await window.google.maps.importLibrary("places");

      const sessionToken = await getSessionToken();

      const request = {
        input: trimmedValue,
        includedRegionCodes: ["fi"],
        sessionToken,
      };

      const response = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

      if (currentRequestId !== requestIdRef.current) {
        return;
      }

      const predictions = response.suggestions.map((suggestion) => suggestion.placePrediction).filter(Boolean);

      setSuggestions(predictions);
    } catch (error) {
      if (currentRequestId === requestIdRef.current) {
        console.error("Error fetching place suggestions:", error);
        setSuggestions([]);
      }
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setIsFetchingSuggestions(false);
      }
    }
  };

  const handleInputChange = (event, value, reason) => {
    setInputValue(value);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (reason === "clear") {
      setSuggestions([]);
      setAddress("");
      setIsFetchingSuggestions(false);

      requestIdRef.current += 1;

      sessionTokenRef.current = null;

      return;
    }

    if (reason !== "input") {
      return;
    }

    const trimmedValue = value?.trim();

    if (!trimmedValue || trimmedValue.length < MIN_SEARCH_LENGTH) {
      setSuggestions([]);
      setIsFetchingSuggestions(false);

      requestIdRef.current += 1;

      return;
    }

    setIsFetchingSuggestions(true);

    debounceTimerRef.current = setTimeout(() => {
      fetchSuggestions(trimmedValue);
    }, DEBOUNCE_DELAY);
  };

  const handlePlaceSelect = async (event, prediction) => {
    if (!prediction) {
      return;
    }

    try {
      const place = prediction.toPlace();

      await place.fetchFields({ fields: ["formattedAddress", "location", "addressComponents"] });

      if (!place.location) {
        console.warn("No location returned for selected place");
        return;
      }

      const lat = place.location.lat();
      const lng = place.location.lng();

      const formatted_address = place.formattedAddress || "";

      const components = place.addressComponents || [];

      const cityComponent = components.find((component) => component.types?.includes("locality") || component.types?.includes("postal_town"));

      const city = cityComponent?.longText || "";

      setAddress(formatted_address);
      setInputValue(formatted_address);

      setLocation({ lat, lng });

      setLocationAddress({ formatted_address, city });
      sessionTokenRef.current = null;

      requestIdRef.current += 1;

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      setSuggestions([]);
    } catch (error) {
      console.error("Error selecting place:", error);
    }
  };

  const handleCurrentClick = () => {
    if (isGettingCurrentLocation) {
      return;
    }

    setIsGettingCurrentLocation(true);
    setLocationFromGeolocation(true);

    if (!navigator.geolocation) {
      console.error("Geolocation is not supported by this browser.");
      setIsGettingCurrentLocation(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;

        fetchAddressByLocation({ lat, lng });
      },
      (error) => {
        console.error("Error getting current location:", error);
        setIsGettingCurrentLocation(false);
      }
    );
  };

  const fetchAddressByLocation = async (location) => {
    const { lat, lng } = location ?? {};

    if (lat == null || lng == null) {
      setIsGettingCurrentLocation(false);
      return;
    }

    try {
      const data = await fetchAddress({ lat, lng }).unwrap();

      setLocationAddress({ formatted_address: data.name.formatted_address, city: data.name.city });

      setLocation({ lat, lng });
    } catch (error) {
      console.error("Error fetching address:", error);
    } finally {
      setIsGettingCurrentLocation(false);
    }
  };

  const handleClearClick = () => {
    setAddress("");
    setInputValue("");
    setSuggestions([]);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    requestIdRef.current += 1;

    sessionTokenRef.current = null;

    emptyLocation();
  };

  return (
    <MUIAutocomplete
      popupIcon={null}
      fullWidth
      options={suggestions}
      value={null}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      onChange={handlePlaceSelect}
      getOptionLabel={(option) => option?.text?.text || ""}
      isOptionEqualToValue={(option, value) => option?.placeId === value?.placeId}
      filterOptions={(options) => options}
      noOptionsText=''
      clearOnBlur={false}
      open={inputValue.trim().length > 0 && !isFetchingSuggestions && suggestions.length > 0}
      disabled={!!locationState.locationAddress || brandsLoading || !isLoaded}
      sx={{
        '& .MuiOutlinedInput-root': {
          paddingRight: '8px !important',
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          InputProps={{
            ...params.InputProps,

            startAdornment: (
              <>
                <InputAdornment position='start'>
                  <PlaceIcon />
                </InputAdornment>

                {params.InputProps.startAdornment}
              </>
            ),

            endAdornment: (
              <InputAdornment position='end'>
                {locationState.locationAddress ? (
                  <IconButton onClick={handleClearClick}>
                    <ClearIcon />
                  </IconButton>
                ) : (
                  <IconButton
                    onClick={handleCurrentClick}
                    disabled={isGettingCurrentLocation}
                  >
                    {isGettingCurrentLocation ? (
                      <CircularProgress size={20} />
                    ) : (
                      <LocationSearchingRoundedIcon />
                    )}
                  </IconButton>
                )}

                {params.InputProps.endAdornment}
              </InputAdornment>
            ),
          }}
          placeholder={t("userLocation.textFieldPlaceholder")}
        />
      )}
      renderOption={(props, option) => (
        <ListItem {...props} key={option.placeId}>
          <Box component='div'>
            <Typography variant='body2'>{option.mainText?.text}</Typography>
            <Typography variant='caption'>{option.secondaryText?.text}</Typography>
          </Box>
        </ListItem>
      )}
    />
  );
}
